/**
 * TermsSheet — полная версия дисклеймера про ответственность за аккаунты.
 *
 * Открывается из SettingsPage (Section "Правовая информация") и при
 * необходимости из других мест. Краткие inline-сноски (AddAccountSheet,
 * TrialOfferSheet, BillingPage) намеренно содержат сокращённую формулировку
 * и указывают «Подробнее в условиях» — пользователь, желающий вникнуть,
 * приходит сюда.
 *
 * Текст составлен максимально нейтрально:
 *   – мы не обещаем срок жизни аккаунта;
 *   – перечисляем реальные факторы вне нашего контроля;
 *   – фиксируем, что бан/лимит не является основанием для возврата средств;
 *   – обозначаем, что юзер использует только свои аккаунты.
 */

import { AlertTriangle, ShieldCheck } from "lucide-react";

import { Sheet } from "@/design";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function TermsSheet({ open, onClose }: Props) {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Условия использования"
      description="Ответственность за Telegram-аккаунты"
    >
      <div className="space-y-5 text-[13px] leading-relaxed text-text-muted">
        <div className="flex items-start gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="size-9 rounded-xl bg-accent-soft text-accent grid place-items-center shrink-0">
            <AlertTriangle className="size-4" />
          </div>
          <div className="text-[12.5px] text-text leading-relaxed">
            SilentPoster — это инструмент для рассылки сообщений через ваши
            собственные Telegram-аккаунты. Срок жизни аккаунта зависит от
            множества факторов, на которые сервис повлиять не может.
          </div>
        </div>

        <Block title="Что влияет на срок жизни аккаунта">
          <ul className="space-y-1.5 pl-1">
            <Li>
              История и репутация IP-адреса, с которого аккаунт был
              зарегистрирован и с которого ранее заходил
            </Li>
            <Li>
              Дата создания и «прогретость» аккаунта: возраст, количество
              контактов, история сообщений и групп
            </Li>
            <Li>
              Поведенческие паттерны — частота отправки, типы чатов, реакция
              получателей и жалобы
            </Li>
            <Li>
              Политика модерации Telegram, которая регулярно обновляется и
              публично не разглашается
            </Li>
          </ul>
        </Block>

        <Block title="Подключая аккаунт к сервису, вы соглашаетесь">
          <ol className="space-y-2 list-decimal pl-5">
            <li>
              Сервис не гарантирует и не может гарантировать сохранность
              аккаунта в&nbsp;Telegram.
            </li>
            <li>
              Бан, ограничение, заморозка или иная блокировка аккаунта со
              стороны Telegram не является основанием для возврата средств
              за подписку.
            </li>
            <li>
              Вы используете только те аккаунты, которые принадлежат вам или
              которыми вы вправе распоряжаться.
            </li>
            <li>
              Вы самостоятельно выбираете темп, шаблоны и список чатов и
              осознаёте, что массовая рассылка повышает риски со стороны
              Telegram.
            </li>
          </ol>
        </Block>

        <Block title="Что мы делаем со своей стороны">
          <ul className="space-y-1.5 pl-1">
            <Li>
              Имитируем «человеческое» поведение: задержки, печать, прочтение
              чатов перед отправкой
            </Li>
            <Li>
              Шифруем сессии и&nbsp;не имеем доступа к&nbsp;вашим личным
              сообщениям
            </Li>
            <Li>
              Рекомендуем умеренный темп и&nbsp;даём настройки тишины,
              чтобы снизить риски
            </Li>
          </ul>
        </Block>

        <div className="flex items-start gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="size-9 rounded-xl bg-accent-soft text-accent grid place-items-center shrink-0">
            <ShieldCheck className="size-4" />
          </div>
          <div className="text-[12px] text-text-muted leading-relaxed">
            Эти условия — стандартная практика сервисов, работающих с&nbsp;Telegram
            MTProto, и&nbsp;обусловлены закрытым характером антифрод-системы
            Telegram. Подключение аккаунта означает согласие с&nbsp;этими условиями.
          </div>
        </div>
      </div>
    </Sheet>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-text-dim uppercase tracking-wider font-medium px-1">
        {title}
      </div>
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3.5 text-[12.5px] text-text-muted">
        {children}
      </div>
    </div>
  );
}

function Li({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <span className="text-accent shrink-0">•</span>
      <span>{children}</span>
    </li>
  );
}
